// Shared heading-slug + table-of-contents extraction. slugify() is used BOTH by
// the Markdown heading renderer (to set each heading's id) and by extractToc()
// (to build the sidebar anchors), so the two always agree — pass the same raw
// heading text to both and the #anchor lands on the right heading.

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/`([^`]+)`/g, "$1") // drop inline-code backticks, keep the word
    .replace(/[*_~]/g, "") // drop emphasis markers
    .replace(/&[a-z]+;/g, "") // drop stray html entities
    .replace(/[^a-z0-9\s-]/g, "") // drop punctuation
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 80);
}

export interface TocItem {
  depth: 2 | 3;
  text: string;
  id: string;
}

// Pull h2/h3 headings out of the raw markdown for the "In this article" rail.
// Skips fenced code blocks so a `## comment` inside a snippet isn't treated as a
// heading. The id uses the same raw text slugify() the renderer sees.
export function extractToc(markdown: string | null | undefined): TocItem[] {
  if (!markdown) return [];
  const items: TocItem[] = [];
  let inFence = false;
  for (const line of markdown.split("\n")) {
    if (/^\s*(```|~~~)/.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    const m = /^(#{2,3})\s+(.+?)\s*#*\s*$/.exec(line);
    if (!m) continue;
    const raw = m[2];
    const text = raw.replace(/`([^`]+)`/g, "$1").replace(/[*_~]/g, "").trim();
    if (!text) continue;
    items.push({ depth: m[1].length as 2 | 3, text, id: slugify(raw) });
  }
  return items;
}
